import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Popconfirm,
  Typography,
  Switch,
  Segmented,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ApiOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MailOutlined,
  WechatOutlined,
  MessageOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import type {
  EmailAccount,
  CreateEmailAccountRequest,
  UpdateEmailAccountRequest,
  ChannelType,
} from '../../types/outreach';
import { CHANNEL_LABELS, CHANNEL_COLORS } from '../../types/outreach';
import { outreachApi } from '../../services/outreach';

const { Title } = Typography;
const { Option } = Select;
const { Password } = Input;

// 渠道图标映射
const CHANNEL_ICONS: Record<ChannelType, React.ReactNode> = {
  email: <MailOutlined />,
  wechat_work: <WechatOutlined />,
  feishu: <MessageOutlined />,
  whatsapp: <WhatsAppOutlined />,
};

// 渠道选项
const CHANNEL_OPTIONS: { label: string; value: ChannelType; icon: React.ReactNode }[] = [
  { label: '邮箱', value: 'email', icon: <MailOutlined /> },
  { label: '企业微信', value: 'wechat_work', icon: <WechatOutlined /> },
  { label: '飞书', value: 'feishu', icon: <MessageOutlined /> },
  { label: 'WhatsApp', value: 'whatsapp', icon: <WhatsAppOutlined /> },
];

export const EmailAccounts: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<EmailAccount[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAccount, setEditingAccount] = useState<EmailAccount | null>(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [channelType, setChannelType] = useState<ChannelType>('email');

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const result = await outreachApi.getEmailAccounts();
      if (result.data) {
        setData(result.data);
      }
    } catch (error) {
      console.error('获取渠道账号列表失败:', error);
      message.error('获取渠道账号列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingAccount(null);
    form.resetFields();
    setChannelType('email');
    form.setFieldsValue({
      channel_type: 'email',
      is_default: false,
    });
    setModalVisible(true);
  };

  const handleEdit = (record: EmailAccount) => {
    setEditingAccount(record);
    setChannelType(record.channel_type);
    
    const formValues: Record<string, any> = {
      channel_type: record.channel_type,
      sender_name: record.sender_name,
      sender_address: record.sender_address,
      is_default: record.is_default,
      status: record.status,
    };

    // 根据渠道类型设置对应的 config 字段
    if (record.config) {
      if (record.channel_type === 'email') {
        formValues.smtp_host = record.config.smtp_host;
        formValues.smtp_port = record.config.smtp_port;
        formValues.smtp_username = record.config.smtp_username;
        formValues.smtp_use_tls = record.config.smtp_use_tls;
        formValues.imap_host = record.config.imap_host;
        formValues.imap_port = record.config.imap_port;
      } else if (record.channel_type === 'wechat_work') {
        formValues.corp_id = record.config.corp_id;
        formValues.agent_id = record.config.agent_id;
        formValues.token = record.config.token;
        formValues.encoding_aes_key = record.config.encoding_aes_key;
      } else if (record.channel_type === 'feishu') {
        formValues.app_id = record.config.app_id;
        formValues.bot_webhook_url = record.config.bot_webhook_url;
        formValues.encrypt_key = record.config.encrypt_key;
        formValues.verification_token = record.config.verification_token;
      } else if (record.channel_type === 'whatsapp') {
        formValues.phone_number_id = record.config.phone_number_id;
        formValues.business_account_id = record.config.business_account_id;
        formValues.webhook_verify_token = record.config.webhook_verify_token;
      }
    }
    
    form.setFieldsValue(formValues);
    setModalVisible(true);
  };

  const handleChannelTypeChange = (value: ChannelType) => {
    setChannelType(value);
    // 清空渠道特定字段
    const currentValues = form.getFieldsValue(['sender_name', 'sender_address', 'is_default', 'status']);
    form.resetFields();
    form.setFieldsValue({
      ...currentValues,
      channel_type: value,
    });
  };

  const buildConfig = (values: any): Record<string, any> => {
    switch (channelType) {
      case 'email':
        return {
          smtp_host: values.smtp_host,
          smtp_port: values.smtp_port,
          smtp_username: values.smtp_username,
          smtp_password: values.smtp_password,
          smtp_use_tls: values.smtp_use_tls,
          imap_host: values.imap_host,
          imap_port: values.imap_port,
          imap_password: values.imap_password,
        };
      case 'wechat_work':
        return {
          corp_id: values.corp_id,
          corp_secret: values.corp_secret,
          agent_id: values.agent_id,
          token: values.token,
          encoding_aes_key: values.encoding_aes_key,
        };
      case 'feishu':
        return {
          app_id: values.app_id,
          app_secret: values.app_secret,
          bot_webhook_url: values.bot_webhook_url,
          encrypt_key: values.encrypt_key,
          verification_token: values.verification_token,
        };
      case 'whatsapp':
        return {
          api_key: values.api_key,
          phone_number_id: values.phone_number_id,
          business_account_id: values.business_account_id,
          webhook_verify_token: values.webhook_verify_token,
        };
      default:
        return {};
    }
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      const config = buildConfig(values);

      if (editingAccount) {
        // 更新
        const updateData: UpdateEmailAccountRequest = {
          sender_name: values.sender_name,
          sender_address: values.sender_address,
          config,
          status: values.status,
          is_default: values.is_default,
        };
        await outreachApi.updateEmailAccount(editingAccount.id, updateData);
        message.success('更新成功');
      } else {
        // 创建
        const createData: CreateEmailAccountRequest = {
          channel_type: channelType,
          sender_name: values.sender_name,
          sender_address: values.sender_address,
          config,
          is_default: values.is_default,
        };
        await outreachApi.createEmailAccount(createData);
        message.success('添加成功');
      }
      setModalVisible(false);
      fetchAccounts();
    } catch (error) {
      console.error('保存渠道账号失败:', error);
      message.error('保存渠道账号失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await outreachApi.deleteEmailAccount(id);
      message.success('删除成功');
      fetchAccounts();
    } catch (error) {
      console.error('删除渠道账号失败:', error);
      message.error('删除渠道账号失败');
    }
  };

  const handleTestConnection = async (id: string) => {
    setTestingId(id);
    try {
      const result = await outreachApi.testEmailAccount(id);
      if (result.data?.success) {
        message.success(result.data.message || '连接测试成功');
      } else {
        message.error(result.data?.message || '连接测试失败');
      }
    } catch (error) {
      console.error('测试连接失败:', error);
      message.error('测试连接失败');
    } finally {
      setTestingId(null);
    }
  };

  const getStatusTag = (status: string) => {
    const statusMap: Record<string, { color: string; text: string; icon: React.ReactNode }> = {
      active: { color: 'success', text: '正常', icon: <CheckCircleOutlined /> },
      inactive: { color: 'default', text: '未激活', icon: <CloseCircleOutlined /> },
      error: { color: 'error', text: '异常', icon: <CloseCircleOutlined /> },
      suspended: { color: 'warning', text: '已暂停', icon: <CloseCircleOutlined /> },
    };
    const config = statusMap[status] || { color: 'default', text: status, icon: null };
    return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
  };

  const getChannelTag = (type: ChannelType) => {
    return (
      <Tag color={CHANNEL_COLORS[type]} icon={CHANNEL_ICONS[type]}>
        {CHANNEL_LABELS[type]}
      </Tag>
    );
  };

  // 获取健康状态标识
  const getHealthTag = (config: any) => {
    if (!config) return null;
    const healthStatus = config.health_status;

    if (!healthStatus) return <Tag>未知</Tag>;

    if (healthStatus === 'healthy') {
      return <Tag color="success" icon={<CheckCircleOutlined />}>健康</Tag>;
    }

    if (healthStatus === 'unhealthy') {
      return <Tag color="error" icon={<CloseCircleOutlined />}>不健康</Tag>;
    }

    return <Tag>未知</Tag>;
  };

  // 获取失败率显示
  const getFailureRateDisplay = (config: any) => {
    if (!config || config.total_sent === 0) return '-';
    const rate = config.failure_rate || 0;
    const color = rate > 0.5 ? 'red' : rate > 0.2 ? 'orange' : 'green';
    return <span style={{ color }}>{(rate * 100).toFixed(1)}%</span>;
  };

  const columns = [
    {
      title: '账号地址',
      dataIndex: 'sender_address',
      key: 'sender_address',
      render: (address: string, record: EmailAccount) => (
        <Space>
          {CHANNEL_ICONS[record.channel_type]}
          <span>{address}</span>
          {record.is_default && <Tag color="blue">默认</Tag>}
        </Space>
      ),
    },
    {
      title: '显示名称',
      dataIndex: 'sender_name',
      key: 'sender_name',
      render: (name: string) => name || '-',
    },
    {
      title: '渠道类型',
      dataIndex: 'channel_type',
      key: 'channel_type',
      width: 120,
      render: (type: ChannelType) => getChannelTag(type),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: '健康状态',
      dataIndex: 'config',
      key: 'health_status',
      width: 100,
      render: (config: any) => getHealthTag(config),
    },
    {
      title: '失败率',
      dataIndex: 'config',
      key: 'failure_rate',
      width: 80,
      render: (config: any) => getFailureRateDisplay(config),
    },
    {
      title: '最后使用',
      dataIndex: 'last_used_at',
      key: 'last_used_at',
      width: 180,
      render: (time: string) => (time ? new Date(time).toLocaleString('zh-CN') : '-'),
    },
    {
      title: '操作',
      key: 'action',
      width: 250,
      render: (_: unknown, record: EmailAccount) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<ApiOutlined />}
            onClick={() => handleTestConnection(record.id)}
            loading={testingId === record.id}
          >
            测试连接
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确认删除"
            description="确定要删除此渠道账号吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 渠道表单配置内容
  const renderChannelConfigForm = () => {
    switch (channelType) {
      case 'email':
        return (
          <>
            <Card title="SMTP配置" size="small" style={{ marginBottom: 16 }}>
              <Form.Item name="smtp_host" label="SMTP服务器">
                <Input placeholder="例如：smtp.gmail.com" />
              </Form.Item>
              <Form.Item name="smtp_port" label="SMTP端口">
                <Input placeholder="例如：587" />
              </Form.Item>
              <Form.Item name="smtp_username" label="SMTP用户名">
                <Input placeholder="请输入SMTP用户名" />
              </Form.Item>
              <Form.Item name="smtp_password" label="SMTP密码">
                <Password placeholder="请输入SMTP密码" />
              </Form.Item>
              <Form.Item name="smtp_use_tls" label="启用TLS" valuePropName="checked">
                <Switch checkedChildren="是" unCheckedChildren="否" />
              </Form.Item>
            </Card>

            <Card title="IMAP配置（用于接收回复）" size="small">
              <Form.Item name="imap_host" label="IMAP服务器">
                <Input placeholder="例如：imap.gmail.com" />
              </Form.Item>
              <Form.Item name="imap_port" label="IMAP端口">
                <Input placeholder="例如：993" />
              </Form.Item>
              <Form.Item name="imap_password" label="IMAP密码">
                <Password placeholder="请输入IMAP密码（通常与SMTP相同）" />
              </Form.Item>
            </Card>
          </>
        );
      case 'wechat_work':
        return (
          <Card title="企业微信配置" size="small">
            <Form.Item
              name="corp_id"
              label="企业 ID"
              rules={[{ required: true, message: '请输入企业 ID' }]}
            >
              <Input placeholder="请输入企业 ID (corp_id)" />
            </Form.Item>
            <Form.Item
              name="corp_secret"
              label="应用 Secret"
              rules={[{ required: !editingAccount, message: '请输入应用 Secret' }]}
            >
              <Password placeholder={editingAccount ? '留空则不修改' : '请输入应用 Secret'} />
            </Form.Item>
            <Form.Item
              name="agent_id"
              label="应用 AgentId"
              rules={[{ required: true, message: '请输入 AgentId' }]}
            >
              <InputNumber placeholder="请输入应用 AgentId" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="token" label="回调 Token">
              <Input placeholder="回调配置用 Token（选填）" />
            </Form.Item>
            <Form.Item name="encoding_aes_key" label="回调 EncodingAESKey">
              <Input placeholder="回调配置用 EncodingAESKey（选填）" />
            </Form.Item>
          </Card>
        );
      case 'feishu':
        return (
          <Card title="飞书配置" size="small">
            <Form.Item
              name="app_id"
              label="应用 ID"
              rules={[{ required: true, message: '请输入应用 ID' }]}
            >
              <Input placeholder="请输入应用 ID (app_id)" />
            </Form.Item>
            <Form.Item
              name="app_secret"
              label="应用 Secret"
              rules={[{ required: !editingAccount, message: '请输入应用 Secret' }]}
            >
              <Password placeholder={editingAccount ? '留空则不修改' : '请输入应用 Secret'} />
            </Form.Item>
            <Form.Item name="bot_webhook_url" label="机器人 Webhook URL">
              <Input placeholder="机器人 Webhook URL（选填）" />
            </Form.Item>
            <Form.Item name="encrypt_key" label="加密密钥">
              <Input placeholder="Encrypt Key（选填）" />
            </Form.Item>
            <Form.Item name="verification_token" label="验证 Token">
              <Input placeholder="Verification Token（选填）" />
            </Form.Item>
          </Card>
        );
      case 'whatsapp':
        return (
          <Card title="WhatsApp配置" size="small">
            <Form.Item
              name="api_key"
              label="API Key"
              rules={[{ required: !editingAccount, message: '请输入 API Key' }]}
            >
              <Password placeholder={editingAccount ? '留空则不修改' : '请输入 API Key'} />
            </Form.Item>
            <Form.Item
              name="phone_number_id"
              label="电话号码 ID"
              rules={[{ required: true, message: '请输入电话号码 ID' }]}
            >
              <Input placeholder="请输入电话号码 ID (phone_number_id)" />
            </Form.Item>
            <Form.Item
              name="business_account_id"
              label="商业账户 ID"
              rules={[{ required: true, message: '请输入商业账户 ID' }]}
            >
              <Input placeholder="请输入商业账户 ID (business_account_id)" />
            </Form.Item>
            <Form.Item name="webhook_verify_token" label="Webhook 验证 Token">
              <Input placeholder="Webhook 验证 Token（选填）" />
            </Form.Item>
          </Card>
        );
      default:
        return null;
    }
  };

  // 获取地址字段标签
  const getAddressLabel = () => {
    switch (channelType) {
      case 'email':
        return '发件邮箱';
      case 'wechat_work':
        return '账号标识';
      case 'feishu':
        return '账号标识';
      case 'whatsapp':
        return '发送号码';
      default:
        return '账号地址';
    }
  };

  // 获取地址字段验证规则
  const getAddressRules = () => {
    if (channelType === 'email') {
      return [
        { required: true, message: '请输入邮箱地址' },
        {
          validator: (_: unknown, value: string) => {
            if (!value || /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) {
              return Promise.resolve();
            }
            return Promise.reject(new Error('请输入有效的邮箱地址'));
          },
        },
      ];
    }
    return [{ required: true, message: `请输入${getAddressLabel()}` }];
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <Title level={4}>渠道账号管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          添加账号
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
          }}
          locale={{ emptyText: '暂无渠道账号' }}
        />
      </Card>

      {/* 新增/编辑Modal */}
      <Modal
        title={editingAccount ? '编辑渠道账号' : '添加渠道账号'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingAccount(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={submitting}
        okText={editingAccount ? '保存' : '添加'}
        cancelText="取消"
        width={650}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {/* 渠道类型选择 */}
          <Form.Item
            name="channel_type"
            label="渠道类型"
            rules={[{ required: true, message: '请选择渠道类型' }]}
          >
            <Segmented
              options={CHANNEL_OPTIONS.map(opt => ({
                label: (
                  <Space>
                    {opt.icon}
                    {opt.label}
                  </Space>
                ),
                value: opt.value,
              }))}
              value={channelType}
              onChange={(val) => handleChannelTypeChange(val as ChannelType)}
              disabled={!!editingAccount}
              block
            />
          </Form.Item>

          <Form.Item name="sender_name" label="显示名称">
            <Input placeholder="请输入显示名称（选填）" maxLength={100} />
          </Form.Item>

          <Form.Item
            name="sender_address"
            label={getAddressLabel()}
            rules={getAddressRules()}
          >
            <Input placeholder={`请输入${getAddressLabel()}`} maxLength={200} />
          </Form.Item>

          <Form.Item name="is_default" label="设为默认" valuePropName="checked">
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>

          {editingAccount && (
            <Form.Item name="status" label="状态">
              <Select placeholder="请选择状态">
                <Option value="active">正常</Option>
                <Option value="inactive">未激活</Option>
                <Option value="error">异常</Option>
              </Select>
            </Form.Item>
          )}

          {/* 渠道特定配置 */}
          {renderChannelConfigForm()}
        </Form>
      </Modal>
    </div>
  );
};
